/**
 * Daily customization: bottom sheet (menu item options before add). RTL via row/start/end; theme-bound colors.
 */
import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { View, Text, StyleSheet, ScrollView } from 'react-native';
import { useTranslation, getApiName } from '../../shared/i18n';
import { useRtl } from '../../shared/i18n/rtl';
import { appTheme, useThemeColors, type ThemeColors } from '../../shared/theme';
import { BottomSheetPopup } from '../../components/common/BottomSheetPopup';
import { createSolidPrimaryCtaLabelStyle,
  createSolidPrimaryCtaSurfaceStyle,
  pillButtonLabelCentered,
} from '../../shared/theme/sharedStyles';
import { createSheetFooterDividerStyle } from '../../shared/theme/styles/sheets';
import { Clickable } from '../../components/common/Clickable';
import type { MenuItemData } from '../../data/domains/menu';

export interface CustomizationOption {
  option_id?: number;
  id?: number;
  name: string;
  is_selected?: boolean;
  disabled?: boolean;
}

export interface CustomizationGroup {
  customization_id: number;
  name: string;
  selection_type: 'single' | 'multiple';
  maximum_selection_limit: number;
  options: CustomizationOption[];
}

export interface DailyCustomizationModalProps {
  visible: boolean;
  menuItem: MenuItemData | null;
  onApply: (customizations: CustomizationGroup[]) => void;
  onCancel: () => void;
}

function getOptionId(opt: CustomizationOption): number {
  return Number((opt as any).option_id ?? (opt as any).id ?? 0);
}

function isValidationSatisfied(groups: CustomizationGroup[], selectedByGroup: Record<number, number[]>): boolean {
  for (let gidx = 0; gidx < groups.length; gidx++) {
    const group = groups[gidx];
    const selected = selectedByGroup[gidx] ?? [];
    const count = selected.length;
    if (group.selection_type === 'single') {
      if (count !== 1) {
        return false;
      }
    } else {
      const max = group.maximum_selection_limit ?? 999;
      if (count < 1 || count > max) {
        return false;
      }
    }
  }
  return true;
}

/** Content-only styles (options + footer); shell uses `createBottomSheetChromeStyles`. */
function createCustomizationSheetBodyStyles(c: ThemeColors) {
  return StyleSheet.create({
    sheetBody: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.sm,
    },
    sheetScroll: {
      flex: 1,
      minHeight: 0,
    },
    sheetScrollContent: {
      paddingBottom: appTheme.spacing.md,
    },
    subtitle: {
      ...appTheme.typography.caption,
      color: c.textSecondary,
      marginBottom: appTheme.spacing.md,
      paddingHorizontal: appTheme.spacing.md,
      textAlign: 'center',
    },
    group: {
      marginBottom: appTheme.spacing.lg,
    },
    groupTitle: {
      fontSize: 13,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      marginBottom: appTheme.spacing.sm,
      color: c.textSecondary,
    },
    optionsList: {
      gap: appTheme.spacing.sm,
    },
    optionRow: {
      borderRadius: appTheme.borderRadius.lg,
      minHeight: 52,
      paddingVertical: 12,
      paddingHorizontal: 14,
      backgroundColor: c.surfaceLight ?? c.surfaceFade,
    },
    optionRowSelected: {
      backgroundColor: c.primaryUltra,
    },
    optionRowDisabled: {
      opacity: 0.45,
    },
    optionRowInner: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    radioOuter: {
      width: 22,
      height: 22,
      borderRadius: 11,
      borderWidth: 2,
      marginEnd: 12,
      justifyContent: 'center',
      alignItems: 'center',
      borderColor: c.primary,
    },
    radioInner: {
      width: 12,
      height: 12,
      borderRadius: 6,
      backgroundColor: c.primary,
    },
    checkboxOuter: {
      width: 22,
      height: 22,
      borderRadius: 6,
      borderWidth: 2,
      marginEnd: 12,
      justifyContent: 'center',
      alignItems: 'center',
      borderColor: c.primary,
    },
    checkboxInner: {
      width: 14,
      height: 14,
      borderRadius: 3,
      backgroundColor: c.primary,
    },
    optionLabel: {
      fontSize: 16,
      flex: 1,
      color: c.text,
      fontWeight: '500',
    },
    optionLabelDisabled: {
      color: c.textSecondary,
    },
    footer: {
      paddingHorizontal: appTheme.spacing.md,
      paddingBottom: appTheme.spacing.xs,
      backgroundColor: c.surface,
      ...createSheetFooterDividerStyle(c),
    },
    footerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: appTheme.spacing.sm,
    },
    cancelBtn: {
      flex: 1,
      height: 52,
      borderRadius: appTheme.borderRadius.xl,
      backgroundColor: c.surfaceLight ?? c.surfaceFade ?? c.surface,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cancelBtnText: {
      ...pillButtonLabelCentered,
      fontSize: 17,
      fontWeight: '600',
      color: c.text,
    },
    applyBtnWrap: {
      flex: 1,
    },
    /** Center label in split footer (RTL / long strings). */
    applyBtnLabel: {
      ...pillButtonLabelCentered,
    },
  });
}

export const DailyCustomizationModal: React.FC<DailyCustomizationModalProps> = ({
  visible,
  menuItem,
  onApply,
  onCancel,
}) => {
  const { t } = useTranslation();
  const rtl = useRtl();
  const [selected_by_group, set_selected_by_group] = useState<Record<number, number[]>>({});

  const colors = useThemeColors() ?? appTheme.colors;
  const bodyStyles = useMemo(() => createCustomizationSheetBodyStyles(colors), [colors]);
  const primaryCtaSurface = useMemo(() => createSolidPrimaryCtaSurfaceStyle(colors), [colors]);
  const primaryCtaLabel = useMemo(() => createSolidPrimaryCtaLabelStyle(colors), [colors]);

  const groups = useMemo(() => {
    const raw = menuItem?.customizations;
    if (!Array.isArray(raw) || raw.length === 0) {
      return [];
    }
    return raw as CustomizationGroup[];
  }, [menuItem?.customizations]);

  useEffect(() => {
    if (visible && groups.length > 0) {
      set_selected_by_group({});
    }
  }, [visible, groups.length]);

  const can_apply = useMemo(() => isValidationSatisfied(groups, selected_by_group), [groups, selected_by_group]);

  const handle_select_single = useCallback((gidx: number, option_id: number) => {
    set_selected_by_group(prev => ({ ...prev, [gidx]: [option_id] }));
  }, []);

  const handle_toggle_multiple = useCallback(
    (gidx: number, option_id: number) => {
      const group = groups[gidx];
      const max = group?.maximum_selection_limit ?? 999;
      set_selected_by_group(prev => {
        const current = prev[gidx] ?? [];
        const has = current.includes(option_id);
        if (has) {
          const next = current.filter(id => id !== option_id);
          return { ...prev, [gidx]: next };
        }
        if (current.length >= max) {
          return prev;
        }
        return { ...prev, [gidx]: [...current, option_id] };
      });
    },
    [groups],
  );

  const handle_apply = useCallback(() => {
    if (!can_apply || groups.length === 0) {
      return;
    }
    const payload = groups.map((group, gidx) => {
      const selected = selected_by_group[gidx] ?? [];
      const options = (group.options ?? []).map((o: CustomizationOption) => ({
        ...o,
        is_selected: selected.includes(getOptionId(o)),
      }));
      return { ...group, options, is_selected: true };
    });
    onApply(payload);
  }, [can_apply, groups, selected_by_group, onApply]);

  const item_name = getApiName((menuItem ?? null) as Record<string, unknown> | null);

  const cancel_btn = (
    <Clickable
      onPress={onCancel}
      style={bodyStyles.cancelBtn}
      accessibilityLabel={t('menu.customizationCancel')}
      accessibilityRole="button">
      <Text style={bodyStyles.cancelBtnText}>{t('menu.customizationCancel')}</Text>
    </Clickable>
  );

  const apply_btn = (
    <View style={bodyStyles.applyBtnWrap}>
      <Clickable
        onPress={handle_apply}
        disabled={!can_apply}
        style={[primaryCtaSurface, !can_apply && { opacity: 0.45 }]}
        accessibilityLabel={t('menu.customizationApply')}
        accessibilityRole="button"
        accessibilityState={{ disabled: !can_apply }}>
        <Text style={[primaryCtaLabel, bodyStyles.applyBtnLabel]}>{t('menu.customizationApply')}</Text>
      </Clickable>
    </View>
  );

  return (
    <BottomSheetPopup
      visible={visible}
      onClose={onCancel}
      onRequestClose={onCancel}
      title={item_name || t('menu.customizationTitle')}
      showDragHandle
      animationType="slide"
      sheetStyle={{ maxHeight: '92%' }}>
      <Text style={bodyStyles.subtitle}>{t('menu.customizationSubtitle')}</Text>

      <ScrollView
        style={bodyStyles.sheetScroll}
        contentContainerStyle={[bodyStyles.sheetScrollContent, bodyStyles.sheetBody]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        {groups.map((group, gidx) => (
          <View key={gidx} style={bodyStyles.group}>
            <Text style={bodyStyles.groupTitle}>{group.name}</Text>
            <View style={bodyStyles.optionsList}>
              {(group.options ?? []).map(opt => {
                const opt_id = getOptionId(opt);
                const selected_ids = selected_by_group[gidx] ?? [];
                const is_selected = selected_ids.includes(opt_id);
                const max = group.maximum_selection_limit ?? 999;
                const is_disabled_multiple = group.selection_type === 'multiple' && !is_selected && selected_ids.length >= max;
                if (group.selection_type === 'single') {
                  return (
                    <Clickable
                      key={opt_id}
                      onPress={() => handle_select_single(gidx, opt_id)}
                      style={[bodyStyles.optionRow, is_selected && bodyStyles.optionRowSelected]}
                      accessibilityLabel={opt.name}
                      accessibilityRole="button"
                      accessibilityState={{ selected: is_selected }}>
                      <View style={bodyStyles.optionRowInner}>
                        <View style={bodyStyles.radioOuter}>{is_selected ? <View style={bodyStyles.radioInner} /> : null}</View>
                        <Text style={bodyStyles.optionLabel}>{opt.name}</Text>
                      </View>
                    </Clickable>
                  );
                }
                return (
                  <Clickable
                    key={opt_id}
                    onPress={() => !is_disabled_multiple && handle_toggle_multiple(gidx, opt_id)}
                    disabled={is_disabled_multiple}
                    style={[
                      bodyStyles.optionRow,
                      is_selected && bodyStyles.optionRowSelected,
                      is_disabled_multiple && bodyStyles.optionRowDisabled,
                    ]}
                    accessibilityLabel={opt.name}
                    accessibilityRole="button"
                    accessibilityState={{ selected: is_selected }}>
                    <View style={bodyStyles.optionRowInner}>
                      <View style={bodyStyles.checkboxOuter}>{is_selected ? <View style={bodyStyles.checkboxInner} /> : null}</View>
                      <Text style={[bodyStyles.optionLabel, is_disabled_multiple && bodyStyles.optionLabelDisabled]}>{opt.name}</Text>
                    </View>
                  </Clickable>
                );
              })}
            </View>
          </View>
        ))}
      </ScrollView>

      <View style={bodyStyles.footer}>
        <View style={[bodyStyles.footerRow, rtl.flipRowStyle]}>
          {cancel_btn}
          {apply_btn}
        </View>
      </View>
    </BottomSheetPopup>
  );
};

export default DailyCustomizationModal;
